import { c as _c } from "react/compiler-runtime";
import type { Sponsor } from 'api-graphql-types/server';
import { formatCents } from 'betterplace-utils';
import classNames from 'classnames';
import { AnimateNumber, ProgressBar } from 'design-system/client';
import { useTranslations, useLocale } from 'next-intl';
import { useMemo } from 'react';
import { useProgressAndDonationsContext } from '../ProgressAndDonationsContext';
import styles from './Progress.module.css';
import type { Maybe } from '@/types';
type ProgressProps = {
  receivedAmountInCents: number;
  receivedDonationsCount: number;
  sponsor?: Maybe<Sponsor>;
  target?: Maybe<number>;
};
function DonationTarget({
  formattedTargetAmount,
  label,
  centerAmounts
}: {
  formattedTargetAmount: string;
  label: string;
  centerAmounts: boolean;
}) {
  return <div className={classNames(styles.goalContainer, {
    [styles.centerAmounts!]: centerAmounts
  })}>
      <div className={styles.number}>{formattedTargetAmount}</div>
      <div className={styles.label}>{label}</div>
    </div>;
}
function CollectedDonationsCount({
  receivedDonationsCount,
  centerAmounts
}: {
  receivedDonationsCount: number;
  centerAmounts: boolean;
}) {
  const t = useTranslations('nextjs.core');
  const locale = useLocale();
  const count = receivedDonationsCount.toLocaleString(locale);
  return <div className={classNames(styles.goalContainer, {
    [styles.centerAmounts!]: centerAmounts
  })}>
      <div className={styles.number}>
        <AnimateNumber animationDurationInMs={1500} maxNumberScale={1.3}>
          {count}
        </AnimateNumber>
      </div>
      <div className={styles.label}>{t('donated')}</div>
    </div>;
}
function CollectedDonationAmount({
  centerAmounts,
  formattedDonationAmount,
  sponsor
}: {
  centerAmounts: boolean;
  formattedDonationAmount: string;
  sponsor?: Maybe<Sponsor>;
}) {
  const t = useTranslations('nextjs.core');
  const label = sponsor?.donatedAmountCaption || t('collected');
  return <div className={classNames(styles.donationContainer, {
    [styles.centerAmounts!]: centerAmounts
  })}>
      <div className={styles.number}>
        <AnimateNumber animationDurationInMs={1500} maxNumberScale={1.3}>
          {formattedDonationAmount}
        </AnimateNumber>
      </div>
      <div className={styles.label}>{label}</div>
    </div>;
}
function VerticalLine() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className={styles.verticalLine} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function Progress({
  sponsor,
  receivedDonationsCount: receivedDonationsCount_,
  receivedAmountInCents: donationAmount_,
  target
}: ProgressProps) {
  const {
    donationCount,
    donationAmountSum
  } = useProgressAndDonationsContext();
  const receivedDonationsCount = donationCount ?? receivedDonationsCount_;
  const receivedAmountInCents = donationAmountSum ?? donationAmount_;
  const locale = useLocale();
  const t = useTranslations('nextjs.receiver_pages.fundraising_event');
  const formattedDonationAmount = useMemo(() => formatCents(locale, receivedAmountInCents), [locale, receivedAmountInCents]);
  const targetAmount = sponsor?.specialAmountInCents && sponsor?.specialAmountCaption ? sponsor.specialAmountInCents : target;
  const formattedTargetAmount = useMemo(() => {
    if (!targetAmount) return;
    return formatCents(locale, targetAmount);
  }, [targetAmount, locale]);
  const showProgressBar = !!target;
  const centerAmounts = !showProgressBar;
  return <section className={styles.container}>
      <div className={classNames(styles.numbersContainer, {
      [styles.centerAmounts!]: centerAmounts
    })}>
        <CollectedDonationAmount sponsor={sponsor} centerAmounts={centerAmounts} formattedDonationAmount={formattedDonationAmount} />
        <VerticalLine />
        {formattedTargetAmount ? <DonationTarget centerAmounts={centerAmounts} formattedTargetAmount={formattedTargetAmount} label={sponsor?.specialAmountInCents && sponsor?.specialAmountCaption ? sponsor.specialAmountCaption : t('our_goal')} /> : <CollectedDonationsCount centerAmounts={centerAmounts} receivedDonationsCount={receivedDonationsCount} />}
      </div>
      {showProgressBar && <ProgressBar value={receivedAmountInCents} max={target} valueText={t('progress_so_far', {
      currentValue: formattedDonationAmount,
      targetValue: formattedTargetAmount ?? ''
    })} invisibleLabelText={`${formattedDonationAmount} / ${formattedTargetAmount}`} />}
    </section>;
}
export default Progress;